import React, { useEffect, useState } from 'react'
import {
  Modal,
  ModalPortal,
  SpacingToken,
  DesignToken,
} from '@livechat/design-system-react-components'
import { css } from '@emotion/react'

import type { IApplication } from '@/apis/apps/interfaces'
import { useOneClickPaymentContext } from '@/context/OneClickPayment'
import useParamSelector from '@/hooks/useParamSelector'
import { useTypedSelector } from '@/store/hooks'
import {
  getPaymentItem,
  isSubscriptionFetched,
  getCardDetails,
  isAppInstalled,
} from '@/store/selectors'
import { usePreventScroll } from '@/utils/browser'

import PurchaseModalFooter from './Footer'
import PurchaseModalHeader from './Header'
import PurchaseModalContent from './Content'

export const modalCss = css`
  width: 95vw;
  max-width: 600px;

  > div {
    padding: var(${SpacingToken.Spacing5}) var(${SpacingToken.Spacing7});
  }

  > div:first-of-type {
    border-bottom: 1px solid var(${DesignToken.BorderBasicTertiary});
    width: 100%;

    > div {
      width: 100%;
    }
  }

  > div:last-of-type {
    border-top: 1px solid var(${DesignToken.BorderBasicTertiary});
    width: 100%;

    > div {
      width: 100%;
    }
  }

  p {
    margin: 0;
  }
`

export const PurchaseModal = () => {
  usePreventScroll()

  const [isActivationRequired, setIsActivationRequired] = useState(false)

  const { onPaymentModalClose, closeModal } = useOneClickPaymentContext()

  const item = useTypedSelector(getPaymentItem) as IApplication
  const hasPaymentFetched = !!useParamSelector(getCardDetails, item?.product)
  const isSubscriptionInfoFetched = useTypedSelector(isSubscriptionFetched)
  const isInstalled = useParamSelector(isAppInstalled, item?.id)

  const hasDirectInstallUrl = item?.directInstallUrl
  const isLoading = (!isSubscriptionInfoFetched && !hasPaymentFetched) || !item

  useEffect(() => {
    if (isInstalled && hasDirectInstallUrl) {
      setIsActivationRequired(true)
    } else if (isInstalled) {
      onPaymentModalClose()
    }
  }, [isInstalled, hasDirectInstallUrl, onPaymentModalClose])

  return (
    <ModalPortal zIndex={1000}>
      <Modal
        data-test="ocp-purchase-modal"
        css={modalCss}
        fullSpaceContent
        onClose={closeModal}
        heading={
          <PurchaseModalHeader
            isLoading={isLoading}
            isActivationRequired={isActivationRequired}
          />
        }
        footer={
          <PurchaseModalFooter
            isLoading={isLoading}
            isActivationRequired={isActivationRequired}
          />
        }
      >
        <PurchaseModalContent
          item={item}
          isLoading={isLoading}
          isActivationRequired={isActivationRequired}
        />
      </Modal>
    </ModalPortal>
  )
}

export default PurchaseModal
